if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();

      this.stickyBar = null;
      this.addToCartButton = null;
      this.quantityDisplay = null;
      this.quantityNumber = null;
      this.productImage = null;
      this.priceDisplay = null;

      this.buyButtonsObserver = null;
      this.variantObserver = null;
      this.variantSyncInterval = null;
      this.scrollClickTimeout = null;
      this.resetTimeout = null;

      this.isStuck = false;
      this.currentQuantity = 1;
      this.variants = null;
      this.variantImages = null;
      this.stickySelectsBound = false;

      this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
      this.handleMainFormChange = this.handleMainFormChange.bind(this);
      this.handleResizeOrScroll = this.handleResizeOrScroll.bind(this);
    }

    connectedCallback() {
      this.stickyBar = this.querySelector('[ref="stickyBar"]');
      this.addToCartButton = this.querySelector('[ref="addToCartButton"]');
      this.quantityDisplay = this.querySelector('[ref="quantityDisplay"]');
      this.quantityNumber = this.querySelector('[ref="quantityNumber"]');
      this.productImage = this.querySelector('[ref="productImage"]');
      this.priceDisplay = this.querySelector('[ref="priceDisplay"]');

      if (!this.stickyBar || !this.addToCartButton) return;

      this.ensureStickyBarInBody();
      this.loadVariantsJson();
      this.loadVariantImageMap();
      this.bindStickyOptionSelects();
      this.bindMainFormWatcher();
      this.getInitialQuantity();
      this.syncStickyDisabledState();
      this.updateStickyTop();

      this.addToCartButton.addEventListener('click', this.handleAddToCartClick);
      window.addEventListener('resize', this.handleResizeOrScroll, { passive: true });
      window.addEventListener('scroll', this.handleResizeOrScroll, { passive: true });
      document.addEventListener('shopify:section:load', this.handleResizeOrScroll);

      this.setupIntersectionObserver();

      const id = this.dataset.currentVariantId || this.getCurrentVariantIdFromForm();
      if (id) {
        this.dataset.currentVariantId = String(id);
        this.updateStickyImageFromMap(id);
        this.syncStickySelectsToVariantId(id);

        const variant = this.findVariantById(id);
        if (variant) {
          this.updateStickyPrice(variant);
        }
      }
    }

    disconnectedCallback() {
      if (this.buyButtonsObserver) this.buyButtonsObserver.disconnect();
      if (this.variantObserver) this.variantObserver.disconnect();
      if (this.variantSyncInterval) clearInterval(this.variantSyncInterval);
      if (this.scrollClickTimeout) clearTimeout(this.scrollClickTimeout);
      if (this.resetTimeout) clearTimeout(this.resetTimeout);

      this.addToCartButton?.removeEventListener('click', this.handleAddToCartClick);
      window.removeEventListener('resize', this.handleResizeOrScroll);
      window.removeEventListener('scroll', this.handleResizeOrScroll);
      document.removeEventListener('shopify:section:load', this.handleResizeOrScroll);
    }

    handleResizeOrScroll() {
      this.updateStickyTop();
    }

    handleAddToCartClick(e) {
      e.preventDefault();

      const mainButton = this.getMainAtcButton();
      if (!mainButton) return;

      const productForm = this.getProductForm();
      const target = productForm || mainButton;

      const rect = target.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = rect.top + scrollTop - 100;

      window.scrollTo({ top: targetY, behavior: 'smooth' });

      if (this.scrollClickTimeout) clearTimeout(this.scrollClickTimeout);

      this.scrollClickTimeout = setTimeout(() => {
        mainButton.click();

        if (this.addToCartButton.dataset.added !== 'true') {
          this.addToCartButton.dataset.added = 'true';
        }

        if (this.resetTimeout) clearTimeout(this.resetTimeout);
        this.resetTimeout = setTimeout(() => {
          this.addToCartButton.removeAttribute('data-added');
        }, 800);
      }, 400);
    }

    ensureStickyBarInBody() {
      if (!this.stickyBar) return;
      if (this.stickyBar.parentElement !== document.body) {
        document.body.appendChild(this.stickyBar);
      }
    }

    getSectionElement() {
      return this.closest('.shopify-section');
    }

    getProductForm() {
      const section = this.getSectionElement();
      if (!section) return null;

      return (
        section.querySelector('form[action*="/cart/add"]') ||
        section.querySelector('product-form form') ||
        section.querySelector('form[data-type="add-to-cart-form"]') ||
        section.querySelector('form.shopify-product-form')
      );
    }

    getMainAtcButton() {
      const form = this.getProductForm();
      if (!form) return null;

      return (
        form.querySelector('.product-form__submit') ||
        form.querySelector('.add-to-cart-button') ||
        form.querySelector('button[name="add"]') ||
        form.querySelector('button[type="submit"]')
      );
    }

    getBuyButtonsBlock() {
      const form = this.getProductForm();
      if (!form) return null;

      return (
        form.closest('.buy-buttons-block') ||
        form.closest('.product-form') ||
        form.closest('product-form') ||
        form
      );
    }

    setupIntersectionObserver() {
      const buyButtonsBlock = this.getBuyButtonsBlock();
      if (!buyButtonsBlock) return;

      this.buyButtonsObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (!entry.isIntersecting) {
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom <= 0) {
            this.showStickyBar();
          } else {
            this.hideStickyBar();
          }
        } else {
          this.hideStickyBar();
        }
      });

      this.buyButtonsObserver.observe(buyButtonsBlock);
    }

    bindMainFormWatcher() {
      const form = this.getProductForm();
      if (!form) return;

      const variantIdInput = form.querySelector('input[name="id"]');

      const apply = () => {
        const id = this.getCurrentVariantIdFromForm();
        if (id) {
          this.dataset.currentVariantId = String(id);
          this.updateStickyImageFromMap(id);
          this.syncStickySelectsToVariantId(id);

          const variant = this.findVariantById(id);
          if (variant) {
            this.updateStickyPrice(variant);
          }
        }

        this.syncStickyDisabledState();
      };

      form.addEventListener('change', this.handleMainFormChange);
      form.addEventListener('input', this.handleMainFormChange);

      if (variantIdInput) {
        this.variantObserver = new MutationObserver(apply);
        this.variantObserver.observe(variantIdInput, {
          attributes: true,
          attributeFilter: ['value']
        });

        this.variantSyncInterval = setInterval(() => {
          const live = variantIdInput.value || '';
          const current = this.dataset.currentVariantId || '';
          if (live && live !== current) apply();
        }, 300);
      }

      apply();
    }

    handleMainFormChange() {
      const id = this.getCurrentVariantIdFromForm();
      if (id) {
        this.dataset.currentVariantId = String(id);
        this.updateStickyImageFromMap(id);
        this.syncStickySelectsToVariantId(id);

        const variant = this.findVariantById(id);
        if (variant) {
          this.updateStickyPrice(variant);
        }
      }

      this.syncStickyDisabledState();
    }

    getCurrentVariantIdFromForm() {
      const form = this.getProductForm();
      if (!form) return '';

      const input = form.querySelector('input[name="id"]');
      return input ? input.value : '';
    }

    getStickyOptionSelects() {
      if (!this.stickyBar) return [];
      return Array.from(this.stickyBar.querySelectorAll('select[data-option-index]'));
    }

    bindStickyOptionSelects() {
      if (this.stickySelectsBound) return;

      const selects = this.getStickyOptionSelects();
      if (!selects.length) return;

      selects.forEach((select) => {
        select.addEventListener('change', () => {
          this.applyVariantFromStickySelects();
        });
      });

      this.stickySelectsBound = true;
    }

    applyVariantFromStickySelects() {
      const selects = this.getStickyOptionSelects();
      if (!selects.length) return;

      const chosen = [];
      selects.forEach((select) => {
        chosen[Number(select.dataset.optionIndex)] = select.value;
      });

      const variant = this.findVariantByOptions(chosen);
      if (!variant) return;

      const form = this.getProductForm();
      if (!form) return;

      const variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput) return;

      variantIdInput.value = String(variant.id);
      variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

      const optionInputs = form.querySelectorAll('input[type="radio"], option');
      if (optionInputs.length) {
        chosen.forEach((value) => {
          const radio = form.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (radio && !radio.checked) radio.click();

          const selectEls = form.querySelectorAll('select');
          selectEls.forEach((mainSelect) => {
            const option = Array.from(mainSelect.options).find((opt) => opt.value === value);
            if (option && mainSelect.value !== value) {
              mainSelect.value = value;
              mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }

      this.dataset.currentVariantId = String(variant.id);
      this.updateStickyImageFromMap(variant.id);
      this.updateStickyPrice(variant);
      this.syncStickyDisabledState();
    }

    loadVariantsJson() {
      const section = this.getSectionElement();
      if (!section) return;

      const sectionId = section.id.replace('shopify-section-', '');
      const el =
        section.querySelector(`#sticky-variants-${sectionId}`) ||
        document.getElementById(`sticky-variants-${sectionId}`);

      if (!el) return;

      try {
        const parsed = JSON.parse(el.textContent.trim());
        if (Array.isArray(parsed)) this.variants = parsed;
      } catch (e) {
        this.variants = null;
      }
    }

    loadVariantImageMap() {
      const section = this.getSectionElement();
      if (!section) return;

      const sectionId = section.id.replace('shopify-section-', '');
      const el =
        section.querySelector(`#variant-image-map-${sectionId}`) ||
        document.getElementById(`variant-image-map-${sectionId}`);

      if (!el) return;

      try {
        const parsed = JSON.parse(el.textContent.trim());
        if (parsed && typeof parsed === 'object') this.variantImages = parsed;
      } catch (e) {
        this.variantImages = null;
      }
    }

    findVariantByOptions(chosen) {
      if (!this.variants || !Array.isArray(this.variants)) return null;

      for (let i = 0; i < this.variants.length; i++) {
        const variant = this.variants[i];
        if (!variant || !Array.isArray(variant.options)) continue;

        let ok = true;
        for (let j = 0; j < chosen.length; j++) {
          if (chosen[j] == null) continue;
          if (variant.options[j] !== chosen[j]) {
            ok = false;
            break;
          }
        }

        if (ok) return variant;
      }

      return null;
    }

    findVariantById(id) {
      if (!this.variants || !Array.isArray(this.variants)) return null;

      const sid = String(id);
      for (let i = 0; i < this.variants.length; i++) {
        const variant = this.variants[i];
        if (variant && String(variant.id) === sid) return variant;
      }

      return null;
    }

    syncStickySelectsToVariantId(id) {
      if (!id) return;

      const variant = this.findVariantById(id);
      if (!variant || !Array.isArray(variant.options)) return;

      const selects = this.getStickyOptionSelects();
      if (!selects.length) return;

      selects.forEach((select) => {
        const idx = Number(select.dataset.optionIndex);
        const value = variant.options[idx];
        if (typeof value === 'string') select.value = value;
      });
    }

    updateStickyImageFromMap(variantId) {
      if (!this.productImage || !this.variantImages || !variantId) return;

      const url = this.variantImages[String(variantId)];
      if (!url) return;

      this.productImage.src = url;
    }

formatMoney(cents) {
  const amount = ((cents || 0) / 100).toFixed(2);
  return `£${amount}`;
}

updateStickyPrice(variant) {
  if (!this.priceDisplay || !variant) return;

  const price = typeof variant.price === 'number' ? variant.price : 0;
  const compareAt = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : 0;
  const onSale = compareAt > price;

  if (onSale) {
    this.priceDisplay.innerHTML = `
      <div class="price">
        <div class="price__container">
          <div class="price__sale">
            <span class="price-item price-item--sale">${this.formatMoney(price)}</span>
            <del class="compare-at-price">${this.formatMoney(compareAt)}</del>
          </div>
        </div>
      </div>
    `;
  } else {
    this.priceDisplay.innerHTML = `
      <div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${this.formatMoney(price)}</span>
          </div>
        </div>
      </div>
    `;
  }
}

    syncStickyDisabledState() {
      const mainButton = this.getMainAtcButton();
      if (!mainButton || !this.addToCartButton) return;

      const disabled = !!mainButton.disabled;
      this.addToCartButton.disabled = disabled;
      this.dataset.variantAvailable = disabled ? 'false' : 'true';
      this.updateButtonText();
    }

    getInitialQuantity() {
      this.currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
      this.updateButtonText();
    }

    updateButtonText() {
      if (!this.quantityNumber || !this.quantityDisplay || !this.addToCartButton) return;

      const available = !this.addToCartButton.disabled;
      this.quantityNumber.textContent = String(this.currentQuantity);

      if (available && this.currentQuantity > 1) {
        this.quantityDisplay.style.display = 'inline';
      } else {
        this.quantityDisplay.style.display = 'none';
      }
    }

    showStickyBar() {
      if (!this.stickyBar) return;
      this.isStuck = true;
      this.stickyBar.dataset.stuck = 'true';
      this.stickyBar.classList.add('show');
    }

    hideStickyBar() {
      if (!this.stickyBar) return;
      this.isStuck = false;
      this.stickyBar.dataset.stuck = 'false';
      this.stickyBar.classList.remove('show');
    }

getHeaderBottom() {
  const candidates = [
    ...document.querySelectorAll(
      '.announcement-bar, .shopify-section-group-header-group, .shopify-section-header, sticky-header, header, .header, [class*="header-wrapper"]'
    ),
  ];

  const visible = candidates.filter((el) => {
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return (
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  });

  if (!visible.length) return null;

  return Math.max(...visible.map((el) => el.getBoundingClientRect().bottom));
}

updateStickyTop() {
  if (!this.stickyBar) return;

  if (window.matchMedia('(max-width: 767px)').matches) {
    this.stickyBar.style.removeProperty('top');
    return;
  }

  const bottom = this.getHeaderBottom();
  if (bottom == null) return;

  this.stickyBar.style.top = `${Math.round(bottom)}px`;
}
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}